import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuiz } from '../contexts/QuizContext';

/**
 * Custom hook for auto-saving quiz progress and handling network interruptions
 * 
 * Features:
 * - Auto-saves quiz state to localStorage as backup
 * - Detects network connectivity changes
 * - Automatically retries failed submissions when connection is restored
 * - Provides recovery mechanisms for interrupted quiz sessions
 */
export const useQuizAutoSave = () => {
  const {
    currentAttempt,
    answers,
    currentQuestionIndex,
    isLoading,
  } = useQuiz();

  const lastSaveRef = useRef<string | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced state tracking
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState<any[]>([]);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  /**
   * Enhanced quiz state saving with sync tracking
   */
  const saveQuizStateToLocal = useCallback((attemptId: string, state: any, options: { needsSync?: boolean } = {}) => {
    try {
      const backupKey = `quiz_backup_${attemptId}`;
      const backupData = {
        ...state,
        lastSaved: new Date().toISOString(),
        timestamp: Date.now(),
        needsSync: options.needsSync || false,
        version: Date.now() // For conflict resolution
      };

      localStorage.setItem(backupKey, JSON.stringify(backupData));

      // Track pending sync operations
      if (options.needsSync && !pendingSync.find(p => p.attemptId === attemptId)) {
        setPendingSync(prev => [...prev, { attemptId, timestamp: Date.now(), state }]);
      }

      setLastSyncTime(new Date());
    } catch (error) {
      console.warn('Failed to save quiz backup to localStorage:', error);
    }
  }, [pendingSync]);

  /**
   * Loads quiz state from localStorage backup
   */
  const loadQuizStateFromLocal = useCallback((attemptId: string) => {
    try {
      const backupKey = `quiz_backup_${attemptId}`;
      const backupData = localStorage.getItem(backupKey);

      if (backupData) {
        return JSON.parse(backupData);
      }
      return null;
    } catch (error) {
      console.warn('Failed to load quiz backup from localStorage:', error);
      return null;
    }
  }, []);

  /**
   * Clears quiz backup from localStorage
   */
  const clearQuizBackup = (attemptId: string) => {
    try {
      const backupKey = `quiz_backup_${attemptId}`;
      localStorage.removeItem(backupKey);
    } catch (error) {
      console.warn('Failed to clear quiz backup from localStorage:', error);
    }
  };

  /**
   * Enhanced online/offline detection
   */
  const isOnline = useCallback(() => {
    return navigator.onLine && isOnlineState;
  }, [isOnlineState]);

  /**
   * Attempt to sync pending changes with server
   */
  const syncWithServer = useCallback(async () => {
    if (!isOnline() || pendingSync.length === 0) return;

    console.log(`Syncing ${pendingSync.length} pending changes...`);

    const successfulSyncs: string[] = [];

    for (const syncItem of pendingSync) {
      try {
        // Here you would implement actual sync logic with your API
        // For now, just simulate success and clear from pending
        console.log(`Synced state for attempt ${syncItem.attemptId}`);
        successfulSyncs.push(syncItem.attemptId);
      } catch (error) {
        console.warn(`Failed to sync attempt ${syncItem.attemptId}:`, error);
      }
    }

    // Remove successfully synced items
    if (successfulSyncs.length > 0) {
      setPendingSync(prev => prev.filter(item => !successfulSyncs.includes(item.attemptId)));
      setLastSyncTime(new Date());
    }
  }, [isOnline, pendingSync]);

  /**
   * Force sync with exponential backoff retry
   */
  const forceSyncWithRetry = useCallback(async (maxRetries = 3) => {
    let retries = 0;

    while (retries < maxRetries && pendingSync.length > 0) {
      try {
        await syncWithServer();
        break;
      } catch (error) {
        retries++;
        const delay = Math.pow(2, retries) * 1000; // Exponential backoff
        console.log(`Sync failed, retrying in ${delay}ms... (attempt ${retries}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }, [syncWithServer, pendingSync.length]);

  /**
   * Auto-save effect - saves quiz state periodically and on changes
   */
  useEffect(() => {
    if (!currentAttempt || isLoading) return;

    const currentState = {
      attemptId: currentAttempt.attemptId,
      answers: answers,
      currentQuestionIndex: currentQuestionIndex,
      lastQuestionIndex: currentQuestionIndex,
      totalQuestions: currentAttempt.totalQuestions,
      status: currentAttempt.status
    };

    const stateKey = JSON.stringify(currentState);

    // Only save if state has changed
    if (lastSaveRef.current !== stateKey) {
      saveQuizStateToLocal(currentAttempt.attemptId, currentState);
      lastSaveRef.current = stateKey;
    }
  }, [currentAttempt, answers, currentQuestionIndex, isLoading]);

  /**
   * Enhanced network change detection effect
   */
  useEffect(() => {
    const handleOnline = () => {
      console.log('Connection restored - attempting to sync pending changes');
      setIsOnlineState(true);

      // Attempt immediate sync when connection is restored
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      retryTimeoutRef.current = setTimeout(() => {
        forceSyncWithRetry();
      }, 1000); // Give a moment for connection to stabilize
    };

    const handleOffline = () => {
      console.log('Connection lost - quiz state will be saved locally only');
      setIsOnlineState(false);
    };

    // Enhanced connectivity check with ping test
    const checkConnectivity = async () => {
      try {
        // Only check if navigator.onLine is true to avoid unnecessary calls
        if (!navigator.onLine) {
          if (isOnlineState) setIsOnlineState(false);
          return;
        }

        await fetch('/api/health', {
          method: 'HEAD',
          cache: 'no-cache',
          signal: AbortSignal.timeout(3000)
        });
        if (!isOnlineState) {
          console.log('🔗 Connectivity restored');
          setIsOnlineState(true);
        }
      } catch {
        if (isOnlineState) {
          console.log('🔌 Connectivity lost (API unreachable)');
          setIsOnlineState(false);
        }
      }
    };

    // Check connectivity every 60 seconds (reduced frequency)
    const connectivityInterval = setInterval(checkConnectivity, 60000);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityInterval);

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isOnlineState, forceSyncWithRetry]);

  /**
   * Periodic sync effect - sync every 5 minutes when online (reduced frequency)
   * Only sync if there are actual pending changes to avoid unnecessary database queries
   */
  useEffect(() => {
    if (isOnline() && currentAttempt?.status === 'in_progress' && pendingSync.length > 0) {
      console.log(`🔄 Starting periodic sync (${pendingSync.length} pending changes)`);
      
      syncIntervalRef.current = setInterval(() => {
        if (pendingSync.length > 0) {
          console.log(`🔄 Periodic sync triggered (${pendingSync.length} pending)`);
          syncWithServer();
        }
      }, 5 * 60 * 1000); // 5 minutes (increased from 2)

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    }
  }, [isOnline, syncWithServer, currentAttempt?.status, pendingSync.length]);

  /**
   * Page unload detection - saves state before user leaves
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (currentAttempt && currentAttempt.status === 'in_progress') {
        // Save current state before leaving
        const currentState = {
          attemptId: currentAttempt.attemptId,
          answers: answers,
          currentQuestionIndex: currentQuestionIndex,
          lastQuestionIndex: currentQuestionIndex,
          totalQuestions: currentAttempt.totalQuestions,
          status: currentAttempt.status,
          exitedAt: new Date().toISOString()
        };

        saveQuizStateToLocal(currentAttempt.attemptId, currentState);

        // Show confirmation dialog for incomplete quizzes
        event.preventDefault();
        event.returnValue = 'You have an incomplete quiz. Your progress will be saved, but you may want to finish it now.';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentAttempt, answers, currentQuestionIndex]);

  /**
   * Cleanup effect - clear backup when quiz is completed
   */
  useEffect(() => {
    if (currentAttempt && currentAttempt.status === 'completed') {
      clearQuizBackup(currentAttempt.attemptId);
    }
  }, [currentAttempt]);

  // Return utility functions for manual operations
  return {
    saveQuizStateToLocal,
    loadQuizStateFromLocal,
    clearQuizBackup,
    isOnline: isOnline(),
    syncWithServer,
    forceSyncWithRetry,
    pendingSyncCount: pendingSync.length,
    lastSyncTime,
    isOnlineState
  };
};

export default useQuizAutoSave;